import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { useForm, usePage } from '@inertiajs/react';
import { AffiliateLayout } from '../../../design-system/components/layouts';
import { Card, CardBody, Button, Input } from '../../../design-system/components';
import {
  affiliate_onboarding_update_slug_path,
  affiliate_check_slug_availability_path,
} from '../../../lib/routes';
export default function Slug({ current_slug, total_steps, product_url }) {
  const { props } = usePage();
  const flash = props.flash;
  const form = useForm({
    slug: current_slug || '',
  });
  const [status, setStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const debounceRef = useRef(undefined);
  const displayUrl = product_url.replace(/^https?:\/\//, '');
  const checkAvailability = useCallback((slug) => {
    if (slug.length < 2) {
      setStatus('invalid');
      setStatusMessage('Must be at least 2 characters');
      return;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(slug)) {
      setStatus('invalid');
      setStatusMessage('Only lowercase letters, numbers, and hyphens allowed');
      return;
    }
    setStatus('checking');
    setStatusMessage('Checking...');
    fetch(`${affiliate_check_slug_availability_path()}?slug=${encodeURIComponent(slug)}`, {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setStatus(data.available ? 'available' : 'unavailable');
        setStatusMessage(data.message);
      })
      .catch(() => {
        setStatus('idle');
        setStatusMessage('');
      });
  }, []);
  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    form.setData('slug', value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) {
      setStatus('idle');
      setStatusMessage('');
      return;
    }
    debounceRef.current = setTimeout(() => checkAvailability(value), 300);
  };
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  const handleSubmit = (e) => {
    e.preventDefault();
    form.post(affiliate_onboarding_update_slug_path());
  };
  const statusIndicator = () => {
    switch (status) {
      case 'checking':
        return <span className="text-xs font-medium text-fg-tertiary">Checking...</span>;
      case 'available':
        return (
          <span className="text-xs font-medium text-green-600 flex items-center gap-1">
            <Check size={14} />
            Available
          </span>
        );
      case 'unavailable':
        return (
          <span className="text-xs font-medium text-red-600 flex items-center gap-1">
            <X size={14} />
            Unavailable
          </span>
        );
      default:
        return null;
    }
  };
  const canSubmit = form.data.slug.length >= 2 && status === 'available' && !form.processing;
  return (
    <AffiliateLayout>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm font-medium text-fg-tertiary mb-2">Step 1 of {total_steps}</p>
          <h1 className="text-2xl font-semibold text-fg tracking-tight">
            Choose your referral slug
          </h1>
          <p className="mt-2 text-sm text-fg-tertiary">
            Your referral link will be{' '}
            <span className="font-mono text-fg">
              {displayUrl}?via=
              <span className="text-fg font-semibold">{form.data.slug || 'yourslug'}</span>
            </span>
          </p>
        </div>

        {flash?.alert && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            {flash.alert}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <Input
                  label="Referral slug"
                  name="slug"
                  id="slug"
                  value={form.data.slug}
                  onChange={handleSlugChange}
                  placeholder="e.g. justin"
                  required
                  autoComplete="off"
                  minLength={2}
                  maxLength={50}
                  error={statusMessage && (status === 'invalid' || status === 'unavailable') ? statusMessage : undefined}
                  helperText="Lowercase letters, numbers, and hyphens only. At least 2 characters."
                  suffix={statusIndicator()}
                />

                {form.errors.slug && <p className="text-sm text-red-600">{form.errors.slug}</p>}
              </div>
            </CardBody>
          </Card>

          <div className="mt-8 flex items-center justify-end">
            <Button type="submit" disabled={!canSubmit} loading={form.processing} size="lg">
              Continue
            </Button>
          </div>
        </form>
      </div>
    </AffiliateLayout>
  );
}
